import type { Transaction } from '@asia/shared';
import { rpc } from './ws';

/** A field the chosen channel needs from the user. */
export interface OfframpField {
  name: string;
  kind: 'pan' | 'phone' | 'text' | 'bank';
  required: boolean;
}

/** A way to cash out, as the provider currently offers it. */
export interface OfframpChannel {
  type: string;
  currency: string;
  country: string | null;
  label: string;
  /** RUB and KGS need a verified customer. KZT does not — the passport goes on the order. */
  needsCustomer?: boolean;
  /** Fiat per USDT, the provider's own commission already folded in. */
  rate: number;
  minUsdt: number | null;
  maxUsdt: number | null;
  minFiat: number | null;
  maxFiat: number | null;
  /** Treasury the USDT is sent to. Present so Max can leave room for the transfer fee. */
  payTo?: string | null;
  fields: OfframpField[];
}

export interface OfframpQuote {
  type: string;
  currency: string;
  fiatAmount: number;
  amountUsdt: number;
  serviceFeeUsdt: number;
  totalUsdt: number;
  rate: number;
  token: 'USDT';
  payTo: string;
}

/** Where the user stands with the provider's verification. */
export interface OfframpProfile {
  status: string;
  ready: boolean;
  verifiedName: string;
  phoneSet: boolean;
  verificationLevel: number;
}

export type PayoutStatus = 'created' | 'pending' | 'processing' | 'completed' | 'failed';

export interface Payout {
  id: string;
  externalId: string;
  type: string;
  country: string | null;
  fiatCurrency: string;
  fiatAmount: number;
  amountUsdt: number;
  serviceFeeUsdt: number;
  totalUsdt: number;
  rate: number;
  recipientMasked: string | null;
  status: PayoutStatus;
  statusReason: string | null;
  refundRequired: boolean;
  reference: string | null;
  createdAt: string;
  updatedAt: string;
}

export const isFinalPayout = (s: PayoutStatus): boolean => s === 'completed' || s === 'failed';

export const offrampRatesApi = () =>
  rpc<{ channels: OfframpChannel[] }>('offramp.rates').then((r) => r.channels);

export const offrampQuoteApi = (type: string, currency: string, usdtAmount: string) =>
  rpc<OfframpQuote>('offramp.quote', { type, currency, usdtAmount });

export const offrampKycApi = () =>
  rpc<{ profile: OfframpProfile | null }>('offramp.kyc').then((r) => r.profile);

export const offrampStartPhoneApi = (phone: string) =>
  rpc<{ profile: OfframpProfile }>('offramp.startPhone', { phone }).then((r) => r.profile);

export const offrampVerifyPhoneApi = (otp: string) =>
  rpc<{ profile: OfframpProfile }>('offramp.verifyPhone', { otp }).then((r) => r.profile);

export const offrampIdentifyApi = (params: {
  firstName: string;
  lastName: string;
  patronymic?: string;
  birthdate: string;
  passport: string;
}) => rpc<{ profile: OfframpProfile }>('offramp.identify', params).then((r) => r.profile);

/** Open the payout. Recipient details go straight to the provider — the server
 *  keeps only a masked tail, so nothing here is stored for reuse. */
export const offrampCreateApi = (params: {
  type: string;
  currency: string;
  fiatAmount: string;
  recipient: Record<string, string>;
  quotedRate: number;
  paymentTransferId: string;
  idempotencyKey: string;
  reauthToken?: string;
}) => rpc<{ payout: Payout }>('offramp.create', params).then((r) => r.payout);

export const offrampGetApi = (id: string) =>
  rpc<{ payout: Payout }>('offramp.get', { id }).then((r) => r.payout);

export const offrampListApi = (limit?: number) =>
  rpc<{ payouts: Payout[] }>('offramp.list', { limit }).then((r) => r.payouts);

/**
 * A cash-out as a history row.
 *
 * The USDT left the wallet as an ordinary transfer, so a settled payout is
 * folded onto that row rather than listed twice; this shape is only for one
 * still in flight, which has no confirmed send to attach to yet.
 */
export function payoutToTransaction(p: Payout): Transaction {
  return {
    id: `off-${p.id}`,
    type: 'send',
    status: p.status === 'completed' ? 'confirmed' : isFinalPayout(p.status) ? 'failed' : 'pending',
    coin: 'USDT',
    chain: 'TRON',
    amount: -p.totalUsdt,
    amountUsd: p.totalUsdt,
    feeUsd: p.serviceFeeUsdt,
    date: p.createdAt,
    storeAmount: p.fiatAmount,
    storeCurrency: p.fiatCurrency,
    ...(p.recipientMasked ? { merchant: p.recipientMasked } : {}),
  };
}
